import { Inject, Injectable } from '@angular/core';
import {
  AGENTS_COLLECTION_NAME,
  AgentDocumentCollections,
  Association,
  AssociationKeys,
  ChangeSourceType,
} from '@ag-common-lib/public-api';
import { FirebaseApp } from 'firebase/app';
import { Timestamp } from 'firebase/firestore';
import { ToastrService } from 'ngx-toastr';
import { CommonFireStoreDao, QueryParam, WhereFilterOperandKeys } from '../dao/CommonFireStoreDao.dao';
import { FIREBASE_APP } from '../injections/firebase-app';
import { localDateTimeFromTimestamp } from '../utils/date-from-timestamp';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';

@Injectable({
  providedIn: 'root',
})
export class AgentAssociationsService {
  public readonly fsDao: CommonFireStoreDao<Association>;

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
    private toastrService: ToastrService,
  ) {
    this.fsDao = new CommonFireStoreDao<Association>(fireBaseApp, AgentAssociationsService.fromFirestore, null);
  }

  getList(agentId: string) {
    const table = AgentAssociationsService.getCollectionPath(agentId);

    return this.fsDao.getList(table);
  }

  getEmergencyContactsList(agentId: string) {
    const table = AgentAssociationsService.getCollectionPath(agentId);
    const qp = [new QueryParam(AssociationKeys.isEmergencyContact, WhereFilterOperandKeys.equal, true)];

    return this.fsDao.getList(table, qp);
  }

  getAll(agentId: string): Promise<Association[]> {
    const table = AgentAssociationsService.getCollectionPath(agentId);

    return this.fsDao.getAll(table);
  }

  create(agentId: string, data: Association, silent = false, action: ChangeSourceType | null = null) {
    const table = AgentAssociationsService.getCollectionPath(agentId);
    Object.assign(data, {
      [BaseModelKeys.actionName]: action,
    });

    return this.fsDao
      .create(data, table)
      .then(response => {
        !silent && this.toastrService.success('Agent Association Successfully Created!');

        return response;
      })

      .catch(e => {
        console.log('e', e, agentId, data);
      });
  }

  update(
    agentId: string,
    documentId: any,
    updates: Partial<Association>,
    silent = false,
    action: ChangeSourceType | null = null,
  ) {
    const table = AgentAssociationsService.getCollectionPath(agentId);
    Object.assign(updates, {
      [BaseModelKeys.actionName]: action,
    });

    return this.fsDao
      .updateFields(updates, documentId, table)
      .then(response => {
        !silent && this.toastrService.success('Agent Association Successfully Updated!');

        return response;
      })

      .catch(e => {
        console.log('e', e);
      });
  }

  delete(agentId: string, documentId: any, action: ChangeSourceType | null = null) {
    const table = AgentAssociationsService.getCollectionPath(agentId);

    return this.fsDao.delete(documentId, table, action).then(response => {
      this.toastrService.success('Agent Association Removed!');
      return response;
    });
  }

  static getCollectionPath(agentId: string) {
    return [AGENTS_COLLECTION_NAME, agentId, AgentDocumentCollections.associations].join('/');
  }

  static readonly fromFirestore = (data): Association => {
    return Object.assign({}, data, {
      dob: localDateTimeFromTimestamp(data?.dob as Timestamp),
    });
  };
}
